// Spec 45 component-behavior — generic card surface (composes
// elevation_raised by default; consumer picks elevation via the
// `elevation` prop). Card is non-interactive by default; pass
// asLink/asButton via composition (wrap with Link or Button asChild)
// for interactive surfaces.
//
// Composition slots:
//   <Card>
//     <CardHeader>...</CardHeader>
//     <CardBody>...</CardBody>
//     <CardFooter>...</CardFooter>
//   </Card>
//
// All four are slot wrappers — they only add data-attrs + className
// hooks for tenant CSS, no behavior.

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export type CardElevation = "flat" | "raised" | "overlay" | "popover" | "modal";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  readonly elevation?: CardElevation;
  /** When true, applies hover/focus styling so the card reads as interactive (consumer wires onClick/onKeyDown). */
  readonly interactive?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ elevation = "raised", interactive = false, className, ...rest }, ref) => (
    <div
      ref={ref}
      data-component="card"
      data-elevation={elevation}
      data-interactive={interactive || undefined}
      className={cx("nai-card", className)}
      {...rest}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} data-component="card-header" className={cx("nai-card-header", className)} {...rest} />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} data-component="card-body" className={cx("nai-card-body", className)} {...rest} />
  ),
);
CardBody.displayName = "CardBody";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} data-component="card-footer" className={cx("nai-card-footer", className)} {...rest} />
  ),
);
CardFooter.displayName = "CardFooter";
